import axios from "axios";
import { templateProviderDiagnostics } from "./utils/template-provider-diagnostics";
import { isMobile, isTablet } from "react-device-detect";
import { getRestApiUrl } from "./utils/backendOverride";
import { endUnauthorizedSession } from "./utils/unauthorizedSession";
import { getActiveSedeId } from "./utils/partnerAccess/partnerStorage";
import {
  SEDE_LOST_REASONS,
  describeSedeTokenFailure,
  discardRejectedDelegatedToken,
  getEffectiveTokenForRequest,
  handleSedeAccessLost,
} from "./utils/partnerAccess/sedeSession";

// Backend `sendBotMessageToUser` spans of 38–62 s were observed on
// 2026-09-22/23 (provider round trips). Without a timeout a hung request kept
// the optimistic bubble grey forever and the agent resent the message; 65 s
// lets the slowest observed send finish and still fails the truly stuck ones.
export const REQUEST_TIMEOUT_MS = 65000;

// Multipart uploads (chat files, template media, profile pictures…) carry the
// file in the request body, and axios' `timeout` covers the upload too. The
// chat dropzone accepts up to 16 MB: on a slow link that alone takes longer
// than the 65 s send budget, and aborting it client-side while the server is
// still processing it makes the agent's retry send the file twice. Five
// minutes still fails a truly stuck upload.
export const UPLOAD_TIMEOUT_MS = 300000;

const API = axios.create({
  baseURL: `${process.env.REACT_APP_REST_API}`,
  timeout: REQUEST_TIMEOUT_MS,
});

// ---------------------------------------------------------------------------
// Message-send instrumentation helpers
//
// The four routes that the user actually hits when clicking "send" in the
// chat get a small set of metadata headers attached. The matching backend
// middleware (messageSendAuditMiddleware in the iventas-backend repo) reads
// them and persists a row to the MessageSendAuditLog Mongo collection so
// the audit log captures network/device/timing data the backend cannot
// infer on its own.
//
// Internal/service routes (sendMessageiVentas, sendMessageAsesoriaCreativa,
// sendPaymentReminder, etc.) are intentionally NOT instrumented.
// ---------------------------------------------------------------------------

const SEND_ROUTES_REGEX = /\/(sendBotMessageToUser|sendMessageWithFileToUser|sendWhatsappTemplateMessage|forwardMessages)/;

function generateTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function getNetworkInfo() {
  if (typeof navigator === "undefined") return { type: "", downlink: "" };
  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!conn) return { type: "", downlink: "" };
  return {
    type: conn.effectiveType || "",
    downlink: conn.downlink != null ? String(conn.downlink) : "",
  };
}

function getDeviceType() {
  if (isTablet) return "tablet";
  if (isMobile) return "mobile";
  return "desktop";
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

API.interceptors.request.use(async (config) => {
  // Partner access: in sede mode the request authenticates with a delegated
  // token (minted on demand); otherwise with the normal JWT, as always. The
  // sede it leaves for is recorded so the response interceptor knows whose
  // failure an UNAUTHORIZED is. A failure to resolve the token rejects here
  // and is shaped by the response interceptor below.
  // eslint-disable-next-line no-param-reassign
  config.sedeCompanyId = getActiveSedeId();
  config.headers.Authorization = `Bearer ${await getEffectiveTokenForRequest()}`;
  // config.headers["ngrok-skip-browser-warning"] = 'true';
  // Resolved per request: the A/B company is routed to the sin-Prisma backend.
  config.baseURL = `${getRestApiUrl()}`;

  // A caller's own `timeout` wins; only the client default is widened.
  if (
    typeof FormData !== "undefined" &&
    config.data instanceof FormData &&
    config.timeout === REQUEST_TIMEOUT_MS
  ) {
    config.timeout = UPLOAD_TIMEOUT_MS;
  }

  const url = config.url || "";
  // A resend after a sede re-mint is the same send: it keeps its trace.
  if (SEND_ROUTES_REGEX.test(url) && !config.sedeAuthRetried) {
    try {
      const traceId = generateTraceId();
      const startedAtClient = Date.now();
      const net = getNetworkInfo();
      // Headers consumed by the backend `messageSendAuditMiddleware`
      config.headers["X-Trace-Id"] = traceId;
      config.headers["X-Client-Started"] = String(startedAtClient);
      config.headers["X-Network-Type"] = net.type;
      config.headers["X-Network-Downlink"] = net.downlink;
      config.headers["X-Device"] = getDeviceType();
      // Stash for the response interceptor (grey→green confirm callback)
      // eslint-disable-next-line no-param-reassign
      config.metadata = { traceId, startedAtClient };
    } catch (e) {
      // Telemetry must NEVER break the request.
      // eslint-disable-next-line no-console
      console.error("[axios-instrumentation] request error (ignored):", e);
    }
  }

  return config;
});

API.interceptors.response.use(
  (response) => {
    // Close the audit cycle: record the moment the message turns green
    // (the FE just received the 200 → updateMessageId sets sent:true).
    const meta = response.config && response.config.metadata;
    if (meta && meta.traceId && meta.startedAtClient) {
      const now = Date.now();
      API.post("/audit/message-confirm", {
        traceId: meta.traceId,
        roundTripMs: now - meta.startedAtClient,
        messageConfirmedAtClient: now,
      }).catch(function () { /* telemetry — never fail */ });
    }
    return response;
  },
  (error) => {
    // The request never left: its sede token could not be resolved.
    const sedeTokenFailure = describeSedeTokenFailure(error);
    if (sedeTokenFailure) {
      return Promise.reject({
        message: sedeTokenFailure.message,
        internalCode: sedeTokenFailure.code,
        httpCode: undefined,
      });
    }

    const data = error.response?.data;
    // A real Error, not a plain object: Sentry's `ignoreErrors` drops
    // "Non-Error promise rejection captured", so plain-object rejections never
    // reached the dashboard project (0 send-path events while the backend
    // logged hundreds of failures). The legacy flat fields are kept unchanged
    // because ~20 consumers read `message` / `internalCode` / `httpCode`
    // directly, and `message` may legitimately be the whole body object.
    const legacyMessage = data?.userMessage || data;
    const jcnError = new Error(
      typeof legacyMessage === "string" ? legacyMessage : error.message
    );
    jcnError.name = "HttpClientError";
    jcnError.message = legacyMessage;
    jcnError.internalCode = data?.internalCode;
    jcnError.httpCode = error.response?.status;
    // axios' own code: `ECONNABORTED` on timeout, `ERR_NETWORK` offline.
    if (typeof error.code === "string") jcnError.code = error.code;
    // Only status + body. Never the axios config: it carries the
    // Authorization header and would end up serialized in Sentry.
    if (error.response) {
      jcnError.response = { status: error.response.status, data };
    }
    // The original AxiosError, for Sentry's linked-errors chain.
    jcnError.cause = error;
    Object.assign(jcnError, templateProviderDiagnostics(data));
    const config = error.config;
    if (jcnError.message === "UNAUTHORIZED" && config?.sedeCompanyId) {
      // Sent as a sede: whatever happens, the normal session is not touched.
      if (getActiveSedeId() !== config.sedeCompanyId) return Promise.reject(jcnError);
      if (config.sedeAuthRetried) {
        handleSedeAccessLost(SEDE_LOST_REASONS.AUTH_REJECTED);
        return Promise.reject(jcnError);
      }
      // First rejection: resend ONCE with a new delegated token (the request
      // interceptor mints it).
      discardRejectedDelegatedToken(config.headers?.Authorization);
      config.sedeAuthRetried = true;
      return API.request(config);
    }
    if (jcnError.message === "UNAUTHORIZED") {
      endUnauthorizedSession();
    }
    return Promise.reject(jcnError);
  }
);

export default API;
